import React, { useState, useEffect } from "react";
import { Pencil, Check, X, MapPin } from "lucide-react";
import { useParams } from "react-router-dom";
import useStationBasicDetails from "../../hooks/useGetStationDetailByStationID";
import useUpdateChargingStationDetails from "../../hooks/useUpdateChargingStationDetails";
import { formatINR } from "../../utils/formatINR";
import { isTetra } from "../../utils/access";

export const getStationLocationFromCache = (stationId) => {
  try {
    const cachedStations = localStorage.getItem(`stations_cache_${stationId}`);
    if (!cachedStations) return "";
    const stationsArray = JSON.parse(cachedStations);
    const station = stationsArray.find((s) => s.stationId === stationId);
    return station ? station.locationName : "";
  } catch (e) {
    console.warn("Failed to read stations cache", e);
    return "";
  }
};

const StationBasicDetails = () => {
  const { stationId } = useParams();
  const { stationDetail, isLoading, error, refetch } = useStationBasicDetails();
  const {
    updateStation,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateChargingStationDetails(stationId);

  const [editableData, setEditableData] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (stationDetail) {
      setEditableData({
        station_name: stationDetail.station_name,
        capacity_kw: stationDetail.capacity_kw,
        number_of_guns: stationDetail.number_of_guns,
        price_per_kwh: stationDetail.price_per_kwh,
        status: stationDetail.status,
      });
      localStorage.setItem(`station_${stationId}`, JSON.stringify(stationDetail));
    }
  }, [stationDetail, stationId]);

  const handleChange = (field, value) => {
    setEditableData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await updateStation(editableData); 
      setIsEditing(false);
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const handleCancel = () => {
    if (stationDetail) {
      setEditableData({
        station_name: stationDetail.station_name,
        capacity_kw: stationDetail.capacity_kw,
        number_of_guns: stationDetail.number_of_guns,
        price_per_kwh: stationDetail.price_per_kwh,
        status: stationDetail.status,
      });
    }
    setIsEditing(false);
  };

  const openLocationInMap = (latitude, longitude) => {
    if (!latitude || !longitude) return;
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, "_blank");
  };

  const locationName =
    getStationLocationFromCache(stationId) ||
    stationDetail?.location_name ||
    "";

  if (isLoading) return <div className="p-6">Loading station details...</div>;
  if (error)
    return (
      <div className="p-6 text-red-500">
        Error fetching station details: {error}
        <button
          onClick={refetch}
          className="ml-3 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
        >
          Retry
        </button>
      </div>
    );
  if (!editableData)
    return <div className="p-6">No station details available.</div>;

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Station Basic Details</h2>
        <div className="flex gap-3">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={isUpdating}
                className="flex items-center gap-1 bg-black text-white px-4 py-2 rounded-md"
              >
                <Check size={16} /> {isUpdating ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1 bg-gray-200 px-4 py-2 rounded-md"
              >
                <X size={16} /> Cancel
              </button>
            </>
          ) : isTetra() ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 bg-black text-white px-5 py-2 rounded-md"
            >
              <Pencil size={16} /> Edit
            </button>
          ) : null}
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
        {/* Station Name */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Station Name</p>
          {isEditing ? (
            <input
              value={editableData.station_name || ""}
              onChange={(e) => handleChange("station_name", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          ) : (
            <p className="text-sm font-medium ">
              <span className="text-gray-800">{editableData.station_name || "Not specified"}</span>
              <span className="text-gray-500 ml-2 text-xs">ID: {stationDetail?.station_id || "N/A"}</span>
            </p>
          )}
        </div>

        {/* Location */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Location</p>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">{locationName || "N/A"}</p>
            {stationDetail?.latitude && stationDetail?.longitude && (
              <button
                onClick={() =>
                  openLocationInMap(
                    stationDetail.latitude,
                    stationDetail.longitude
                  )
                }
                className="p-1 hover:bg-gray-100 rounded-full transition"
                title="Open in Google Maps"
              >
                <MapPin size={18} className="text-red-500" />
              </button>
            )}
          </div>
        </div>

        {/* Address */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Address</p>
          <p
            className="text-sm font-medium break-words text-gray-700"
            title={`${stationDetail?.address_line1 || ""} ${stationDetail?.address_line2 || ""}`}
          >
            {stationDetail?.address_line1 || ""}
            {stationDetail?.address_line2 && `, ${stationDetail.address_line2}`}
          </p>
        </div>

        {/* Capacity */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Capacity (kW)</p>
          <p className="text-sm font-medium">
            {editableData.capacity_kw || stationDetail?.capacity_kw || "-"}
          </p>
        </div>


        {/* Status */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Status</p>
          {isEditing ? (
            <select
              value={editableData.status || ""}
              onChange={(e) => handleChange("status", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          ) : (
            <span
              className={`px-3 py-1.5 text-xs rounded-full font-medium border ${
                editableData.status === "active"
                  ? "bg-green-100 text-green-800 border-green-300"
                  : "bg-red-100 text-red-800 border-red-300"
              }`}
            >
              {editableData.status}
            </span>
          )}
        </div>

        {/* Price */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Price (per kWh)</p>
          {isEditing ? (
            <input
              type="number"
              step="0.01"
              value={editableData.price_per_kwh || ""}
              onChange={(e) => handleChange("price_per_kwh", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          ) : (
            <p className="text-sm font-medium">
              {formatINR(editableData.price_per_kwh, "")}
            </p>
          )}
        </div>
      </div>

      {updateError && <p className="text-red-500 mt-4">Error: {updateError}</p>}
    </div>
  );
};

export default StationBasicDetails;
